// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — My Training
// Personal training view with compliance ring, assigned plan,
// all trainings, completion history, log & detail modals
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Text, Group, Badge, Button, Modal, Stack,
  SimpleGrid, TextInput, Textarea, Loader, Center, ActionIcon,
  RingProgress, Tabs, Table,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconClipboardList, IconBook, IconCircleCheck, IconSearch,
  IconSchool, IconClock, IconCircleX, IconCheck, IconEye,
  IconRefresh, IconCalendar, IconInbox,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useBlackboardStore, type BBMessage } from '../../store/blackboardStore';
import { useAuthStore } from '../../store/authStore';
import type { Training, TrainingRecord } from '../../store/blackboardStore';

// ── Constants ──────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  compliant: '#16a34a',
  completed: '#16a34a',
  expiring: '#d97706',
  urgent: '#ea580c',
  expired: '#dc2626',
  overdue: '#dc2626',
  notStarted: '#868e96',
};

const CATEGORY_COLORS: Record<string, string> = {
  'NIMS/ICS': 'blue',
  'Professional Development': 'violet',
  'Hazmat/Safety': 'orange',
  'Health & Safety': 'red',
  Operations: 'teal',
  'Community Programs': 'green',
  Weather: 'cyan',
  Communications: 'indigo',
  Custom: 'gray',
};

// ── Helpers ────────────────────────────────────────────────────

interface StatusInfo {
  label: string;
  color: string;
  key: string;
}

function getStatusInfo(record: TrainingRecord | undefined, training: Training | undefined): StatusInfo {
  if (!record || !record.completedDate) {
    return { label: 'Not Started', color: STATUS_COLORS.notStarted, key: 'notStarted' };
  }

  // Has completion
  const isOneTime = (training?.frequencyDays === 0) && !record.expiresDate;
  if (isOneTime || !record.expiresDate) {
    return { label: 'Completed', color: STATUS_COLORS.completed, key: 'completed' };
  }

  const expires = dayjs(record.expiresDate);
  const now = dayjs();
  const daysUntil = expires.diff(now, 'day');

  if (daysUntil < 0) return { label: 'Expired', color: STATUS_COLORS.expired, key: 'expired' };
  if (daysUntil <= 7) return { label: 'Urgent', color: STATUS_COLORS.urgent, key: 'urgent' };
  if (daysUntil <= 30) return { label: 'Expiring', color: STATUS_COLORS.expiring, key: 'expiring' };
  return { label: 'Compliant', color: STATUS_COLORS.compliant, key: 'compliant' };
}

function isCompliantStatus(key: string): boolean {
  return key === 'compliant' || key === 'completed';
}

function formatDate(d: string | null | undefined): string | null {
  if (!d) return null;
  const parsed = dayjs(d);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
}

// ── Training Card ──────────────────────────────────────────────

interface TrainingCardProps {
  training: Training;
  record: TrainingRecord | undefined;
  onLogCompletion: (training: Training) => void;
  onViewDetails: (training: Training, record: TrainingRecord) => void;
}

function TrainingCard({ training, record, onLogCompletion, onViewDetails }: TrainingCardProps) {
  const status = getStatusInfo(record, training);
  const completedDate = formatDate(record?.completedDate);
  const expiresDate = formatDate(record?.expiresDate);
  const hasRecord = !!record?.completedDate;

  return (
    <Paper
      p={0}
      withBorder
      shadow="sm"
      radius="md"
      style={{
        overflow: 'hidden',
        display: 'flex',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(17,45,78,.06), 0 12px 36px rgba(17,45,78,.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Left status bar */}
      <Box style={{ width: 4, minHeight: '100%', backgroundColor: status.color, flexShrink: 0 }} />

      <Box p="md" style={{ flex: 1 }}>
        {/* Title row */}
        <Group justify="space-between" align="flex-start" mb={6}>
          <Text fw={600} size="sm" style={{ flex: 1, paddingRight: 8 }}>
            {training.name}
          </Text>
          <Badge color={CATEGORY_COLORS[training.category] || 'gray'} size="sm" variant="light">
            {training.category || 'Custom'}
          </Badge>
        </Group>

        {/* Info rows */}
        <Box mb="sm">
          {completedDate ? (
            <Group gap={6}>
              <IconCircleCheck size={11} color={status.color} />
              <Text size="xs" c="dimmed">Completed on {completedDate}</Text>
            </Group>
          ) : (
            <Group gap={6}>
              <IconCircleX size={11} color={STATUS_COLORS.notStarted} />
              <Text size="xs" c="dimmed">Not yet completed</Text>
            </Group>
          )}
          {expiresDate && (
            <Group gap={6} mt={2}>
              <IconClock size={11} color={status.color} />
              <Text size="xs" c="dimmed">Expires {expiresDate}</Text>
            </Group>
          )}
        </Box>

        {/* Footer row */}
        <Group justify="space-between" align="center">
          <Badge
            variant="light"
            size="sm"
            style={{ backgroundColor: status.color + '18', color: status.color }}
          >
            {status.label}
          </Badge>
          <Button
            variant="light"
            color="blue"
            size="compact-xs"
            onClick={() => hasRecord && record ? onViewDetails(training, record) : onLogCompletion(training)}
            leftSection={hasRecord ? <IconEye size={10} /> : <IconClipboardList size={10} />}
          >
            {hasRecord ? 'View Details' : 'Log Completion'}
          </Button>
        </Group>
      </Box>
    </Paper>
  );
}

// ── Section Header ─────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Text
      size="10px"
      fw={700}
      c="dimmed"
      mb="xs"
      pb={6}
      style={{ textTransform: 'uppercase', letterSpacing: 2, borderBottom: '1px solid #dee2e6' }}
    >
      {children}
    </Text>
  );
}

// ── Main Component ─────────────────────────────────────────────

export default function MyTraining() {
  const { user } = useAuthStore();
  const {
    trainings, trainingRecords, assignments, messages, classrooms, loading,
    loadTrainings, loadTrainingRecords, loadAssignments, loadClassrooms,
    loadMessages, markMessageRead,
    createTrainingRecord,
  } = useBlackboardStore();

  // Tab state
  const [activeTab, setActiveTab] = useState<string | null>('assigned');

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Log Completion modal
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logTraining, setLogTraining] = useState<Training | null>(null);
  const [logDate, setLogDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [logNotes, setLogNotes] = useState('');
  const [logSaving, setLogSaving] = useState(false);

  // View Details modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewTraining, setViewTraining] = useState<Training | null>(null);
  const [viewRecord, setViewRecord] = useState<TrainingRecord | null>(null);

  // ── Data loading ────────────────────────────────────────────
  useEffect(() => {
    loadTrainings();
    loadTrainingRecords();
    loadAssignments();
    loadMessages();
    loadClassrooms();
  }, [loadTrainings, loadTrainingRecords, loadAssignments, loadMessages, loadClassrooms]);

  // ── Current user matching ────────────────────────────────────
  const currentEmail = user?.email?.toLowerCase() || '';
  const currentName = user?.fullName || '';

  // ── Inbox messages for current user ─────────────────────────
  const myMessages = useMemo(() =>
    messages
      .filter(m => m.recipientEmail?.toLowerCase() === currentEmail && (m.channel === 'inbox' || m.channel === 'both'))
      .sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || '')),
  [messages, currentEmail]);
  const unreadCount = myMessages.filter(m => !m.readAt).length;
  const [viewMessage, setViewMessage] = useState<BBMessage | null>(null);

  // Filter records for current user
  const myRecords = useMemo(() => {
    return trainingRecords.filter(r => {
      const rEmail = (r.userName || '').toLowerCase();
      const rUserId = (r.userId || '').toLowerCase();
      return rEmail === currentEmail || rUserId === currentEmail ||
        r.userName === currentName;
    });
  }, [trainingRecords, currentEmail, currentName]);

  // Map trainingId -> most recent record
  const recordMap = useMemo(() => {
    const map: Record<string, TrainingRecord> = {};
    myRecords.forEach(r => {
      const existing = map[r.trainingId];
      if (!existing || (r.completedDate && (!existing.completedDate ||
        dayjs(r.completedDate).isAfter(dayjs(existing.completedDate))))) {
        map[r.trainingId] = r;
      }
    });
    return map;
  }, [myRecords]);

  // Training map for quick lookup
  const trainingMap = useMemo(() => {
    const map: Record<string, Training> = {};
    trainings.forEach(t => { map[String(t.id)] = t; });
    return map;
  }, [trainings]);

  // My assignments
  const myAssignments = useMemo(() => {
    return assignments.filter(a => {
      const aUserId = (a.userId || '').toLowerCase();
      return aUserId === currentEmail;
    });
  }, [assignments, currentEmail]);

  // Assigned trainings: individually assigned + active trainings
  const assignedTrainings = useMemo(() => {
    const assignedTrainingIds = new Set<string>();
    myAssignments.forEach(a => {
      (a.trainingIds || '').split(',').filter(Boolean).forEach(id => assignedTrainingIds.add(id.trim()));
    });

    const result: Training[] = [];
    const seen = new Set<number>();

    // Individually assigned
    assignedTrainingIds.forEach(id => {
      const t = trainingMap[id];
      if (t && !seen.has(t.id)) {
        seen.add(t.id);
        result.push(t);
      }
    });

    // Role-based required (if no specific assignments, show all active)
    if (result.length === 0) {
      trainings.filter(t => t.active !== 'false').forEach(t => {
        if (!seen.has(t.id)) {
          seen.add(t.id);
          result.push(t);
        }
      });
    }

    return result;
  }, [myAssignments, trainingMap, trainings]);

  // ── Compliance ring ──────────────────────────────────────────
  const compliancePercent = useMemo(() => {
    if (assignedTrainings.length === 0) return 100;
    let compliant = 0;
    assignedTrainings.forEach(t => {
      const rec = recordMap[String(t.id)];
      const status = getStatusInfo(rec, t);
      if (isCompliantStatus(status.key)) compliant++;
    });
    return Math.round((compliant / assignedTrainings.length) * 100);
  }, [assignedTrainings, recordMap]);

  const complianceColor =
    compliancePercent >= 80 ? STATUS_COLORS.compliant
      : compliancePercent >= 50 ? STATUS_COLORS.expiring
        : STATUS_COLORS.expired;

  const complianceLabel =
    compliancePercent >= 80 ? 'On Track'
      : compliancePercent >= 50 ? 'Needs Attention'
        : 'Action Required';

  // ── Completed trainings ──────────────────────────────────────
  const completedTrainings = useMemo(() => {
    return myRecords
      .filter(r => r.completedDate)
      .sort((a, b) => {
        const da = a.completedDate ? new Date(a.completedDate).getTime() : 0;
        const db2 = b.completedDate ? new Date(b.completedDate).getTime() : 0;
        return db2 - da;
      });
  }, [myRecords]);

  // ── Filtered all trainings ───────────────────────────────────
  const filteredAllTrainings = useMemo(() => {
    return trainings.filter(t => {
      if (t.active === 'false') return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(t.name || '').toLowerCase().includes(q) && !(t.category || '').toLowerCase().includes(q)) return false;
      }
      if (categoryFilter && t.category !== categoryFilter) return false;
      return true;
    });
  }, [trainings, search, categoryFilter]);

  // Unique categories
  const categories = useMemo(() => {
    const cats = new Set(trainings.map(t => t.category).filter(Boolean));
    return [...cats].sort();
  }, [trainings]);

  // ── Log Completion ───────────────────────────────────────────
  const openLogModal = useCallback((training: Training) => {
    setLogTraining(training);
    setLogDate(dayjs().format('YYYY-MM-DD'));
    setLogNotes('');
    setLogModalOpen(true);
  }, []);

  const closeLogModal = useCallback(() => {
    setLogModalOpen(false);
    setLogTraining(null);
    setLogDate(dayjs().format('YYYY-MM-DD'));
    setLogNotes('');
  }, []);

  const handleLogSave = async () => {
    if (!logTraining || !logDate) {
      notifications.show({ title: 'Missing date', message: 'Please select a completion date.', color: 'yellow' });
      return;
    }

    setLogSaving(true);
    try {
      // Calculate expiration
      let expiresDate = '';
      if (logTraining.frequencyDays && logTraining.frequencyDays > 0) {
        expiresDate = dayjs(logDate).add(logTraining.frequencyDays, 'day').format('YYYY-MM-DD');
      }

      await createTrainingRecord({
        userId: currentEmail,
        trainingId: String(logTraining.id),
        trainingName: logTraining.name,
        userName: currentName || currentEmail,
        completedDate: dayjs(logDate).format('YYYY-MM-DD'),
        expiresDate,
        status: 'completed',
        notes: logNotes,
        certificateId: '',
      });

      notifications.show({ title: 'Success', message: 'Training completion logged successfully.', color: 'green' });
      closeLogModal();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: `Failed to save completion: ${err instanceof Error ? err.message : 'Unknown error'}`,
        color: 'red',
      });
    } finally {
      setLogSaving(false);
    }
  };

  // ── View Details ─────────────────────────────────────────────
  const openViewModal = useCallback((training: Training, record: TrainingRecord) => {
    setViewTraining(training);
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setViewModalOpen(false);
    setViewTraining(null);
    setViewRecord(null);
  }, []);

  const handleRenew = () => {
    if (!viewTraining) return;
    closeViewModal();
    openLogModal(viewTraining);
  };

  // ── Loading ──────────────────────────────────────────────────
  if (loading && trainings.length === 0) {
    return (
      <Center h={400}>
        <Loader color="blue" />
      </Center>
    );
  }

  const viewStatus = viewRecord ? getStatusInfo(viewRecord, viewTraining ?? undefined) : null;
  const viewIsExpiredOrExpiring = viewStatus && ['expired', 'urgent', 'expiring'].includes(viewStatus.key);

  // ── Render ───────────────────────────────────────────────────
  return (
    <Box>
      {/* Header with compliance ring */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Text size="10px" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: 2 }}>
            Personal
          </Text>
          <Text size="xl" fw={700} c="#1a365d">My Training</Text>
        </Box>
        <Group gap="sm" align="center">
          <RingProgress
            size={56}
            thickness={5}
            roundCaps
            sections={[{ value: compliancePercent, color: complianceColor }]}
            label={
              <Text size="xs" fw={700} ta="center" c={complianceColor}>
                {compliancePercent}%
              </Text>
            }
          />
          <Box>
            <Text size="xs" c="dimmed">Overall Compliance</Text>
            <Badge
              variant="light"
              size="sm"
              style={{ backgroundColor: complianceColor + '18', color: complianceColor }}
            >
              {complianceLabel}
            </Badge>
          </Box>
        </Group>
      </Group>

      {/* Upcoming Classes */}
      {(() => {
        const now = dayjs();
        // Find classes user is registered for
        const myUpcoming = classrooms.filter(c => {
          if (!c.classDate || c.status === 'cancelled' || c.status === 'completed') return false;
          if (dayjs(c.classDate).isBefore(now, 'day')) return false;
          try {
            const reg = JSON.parse(c.registeredUsers || '[]');
            const wl = JSON.parse(c.waitlist || '[]');
            const userEmail = currentEmail;
            return [...reg, ...wl].some((r: any) =>
              r.email?.toLowerCase() === userEmail || r.userId === String(user?.id)
            );
          } catch { return false; }
        }).sort((a, b) => (a.classDate || '').localeCompare(b.classDate || ''));

        if (myUpcoming.length === 0) return null;
        return (
          <Paper p="md" withBorder mb="md" style={{ borderLeft: '4px solid #3b82f6' }}>
            <Text fw={600} size="sm" mb="xs">
              <IconCalendar size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Upcoming Classes ({myUpcoming.length})
            </Text>
            <Stack gap={4}>
              {myUpcoming.slice(0, 5).map(c => (
                <Group key={c.id} justify="space-between" p="xs"
                  style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 6 }}>
                  <div>
                    <Text size="sm" fw={500}>{c.title}</Text>
                    <Text size="xs" c="dimmed">
                      {dayjs(c.classDate).format('ddd, MMM D')}
                      {c.classTime ? ` at ${c.classTime}` : ''}
                      {c.location ? ` — ${c.location}` : ''}
                    </Text>
                  </div>
                  <Badge size="sm" variant="light" color="blue">
                    {dayjs(c.classDate).diff(now, 'day')} days
                  </Badge>
                </Group>
              ))}
            </Stack>
          </Paper>
        );
      })()}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="assigned" leftSection={<IconClipboardList size={14} />}>
            Assigned Plan
          </Tabs.Tab>
          <Tabs.Tab value="all" leftSection={<IconBook size={14} />}>
            All Trainings
          </Tabs.Tab>
          <Tabs.Tab value="completed" leftSection={<IconCircleCheck size={14} />}>
            Completed
          </Tabs.Tab>
          <Tabs.Tab value="inbox" leftSection={<IconInbox size={14} />}>
            Inbox {unreadCount > 0 && <Badge size="xs" circle color="red" ml={4}>{unreadCount}</Badge>}
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Tab 1: Assigned Plan ────────────────────────────────── */}
        <Tabs.Panel value="assigned">
          <SectionHeader>Assigned Training Plan</SectionHeader>
          {assignedTrainings.length === 0 ? (
            <Paper p="xl" mt="sm" withBorder shadow="sm" radius="md">
              <Center>
                <Box style={{ textAlign: 'center' }}>
                  <IconSchool size={32} color="#868e96" style={{ marginBottom: 8 }} />
                  <Text c="dimmed" size="sm" mt="xs">No trainings are currently assigned to you.</Text>
                  <Text c="dimmed" size="xs" mt={4}>
                    Check the "All Trainings" tab to browse and self-log completions.
                  </Text>
                </Box>
              </Center>
            </Paper>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mt="sm">
              {assignedTrainings.map(training => (
                <TrainingCard
                  key={training.id}
                  training={training}
                  record={recordMap[String(training.id)]}
                  onLogCompletion={openLogModal}
                  onViewDetails={openViewModal}
                />
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        {/* ── Tab 2: All Trainings ────────────────────────────────── */}
        <Tabs.Panel value="all">
          <SectionHeader>Training Library</SectionHeader>

          {/* Filters */}
          <Group mb="md" mt="sm">
            <TextInput
              placeholder="Search trainings..."
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1, maxWidth: 320 }}
              size="sm"
            />
            <Box>
              <Group gap={6}>
                <Badge
                  variant={categoryFilter === '' ? 'filled' : 'light'}
                  color="blue"
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setCategoryFilter('')}
                >
                  All
                </Badge>
                {categories.map(cat => (
                  <Badge
                    key={cat}
                    variant={categoryFilter === cat ? 'filled' : 'light'}
                    color={CATEGORY_COLORS[cat] || 'gray'}
                    size="sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setCategoryFilter(prev => prev === cat ? '' : cat)}
                  >
                    {cat}
                  </Badge>
                ))}
              </Group>
            </Box>
          </Group>

          {filteredAllTrainings.length === 0 ? (
            <Paper p="xl" withBorder shadow="sm" radius="md">
              <Center>
                <Text c="dimmed" size="sm">No trainings match your search.</Text>
              </Center>
            </Paper>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
              {filteredAllTrainings.map(training => (
                <TrainingCard
                  key={training.id}
                  training={training}
                  record={recordMap[String(training.id)]}
                  onLogCompletion={openLogModal}
                  onViewDetails={openViewModal}
                />
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        {/* ── Tab 3: Completed ────────────────────────────────────── */}
        <Tabs.Panel value="completed">
          <SectionHeader>Completion History</SectionHeader>

          {completedTrainings.length === 0 ? (
            <Paper p="xl" mt="sm" withBorder shadow="sm" radius="md">
              <Center>
                <Box style={{ textAlign: 'center' }}>
                  <IconBook size={32} color="#868e96" style={{ marginBottom: 8 }} />
                  <Text c="dimmed" size="sm" mt="xs">No completed trainings yet.</Text>
                </Box>
              </Center>
            </Paper>
          ) : (
            <Paper withBorder shadow="sm" radius="md" mt="sm" style={{ overflow: 'hidden' }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Training Name</Table.Th>
                    <Table.Th>Category</Table.Th>
                    <Table.Th>Completed</Table.Th>
                    <Table.Th>Expires</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {completedTrainings.map(rec => {
                    const training = trainingMap[rec.trainingId];
                    const status = getStatusInfo(rec, training);

                    return (
                      <Table.Tr key={rec.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {rec.trainingName || training?.name || 'Unknown'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={CATEGORY_COLORS[training?.category || ''] || 'gray'}
                            size="sm"
                            variant="light"
                          >
                            {training?.category || 'Custom'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatDate(rec.completedDate) || '\u2014'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatDate(rec.expiresDate) || 'Never'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            variant="light"
                            size="sm"
                            style={{ backgroundColor: status.color + '18', color: status.color }}
                          >
                            {status.label}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant="light"
                            color="blue"
                            size="sm"
                            onClick={() =>
                              openViewModal(
                                training || { id: Number(rec.trainingId), name: rec.trainingName, category: '', description: '', frequencyDays: 0, requiredForRoles: '', active: '', datecreated: '' },
                                rec,
                              )
                            }
                          >
                            <IconEye size={14} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Tabs.Panel>

        {/* ── Tab 4: Inbox ──────────────────────────────────────── */}
        <Tabs.Panel value="inbox">
          <Stack gap="md">
            {myMessages.length === 0 ? (
              <Paper p="xl" withBorder ta="center">
                <IconInbox size={40} color="gray" style={{ opacity: 0.3 }} />
                <Text c="dimmed" mt="sm">No messages yet.</Text>
              </Paper>
            ) : (
              myMessages.map(m => {
                const isUnread = !m.readAt;
                return (
                  <Paper key={m.id} p="md" withBorder
                    style={{
                      cursor: 'pointer',
                      borderLeft: isUnread ? '3px solid var(--mantine-color-blue-5)' : undefined,
                      background: isUnread ? 'var(--mantine-color-blue-0)' : undefined,
                    }}
                    onClick={() => {
                      setViewMessage(m);
                      if (isUnread) markMessageRead(m.id);
                    }}>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        {isUnread && <Badge size="xs" color="blue">New</Badge>}
                        <Text size="sm" fw={isUnread ? 700 : 500}>{m.subject}</Text>
                      </Group>
                      <Text size="xs" c="dimmed">{m.sentAt ? dayjs(m.sentAt).format('MMM D, h:mm A') : ''}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">From: {m.senderName}</Text>
                  </Paper>
                );
              })
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* ── Message Detail Modal ──────────────────────────────── */}
      <Modal opened={!!viewMessage} onClose={() => setViewMessage(null)}
        title={viewMessage?.subject || 'Message'} size="lg">
        {viewMessage && (
          <Stack gap="sm">
            <Group gap="lg">
              <div>
                <Text size="xs" c="dimmed">From</Text>
                <Text size="sm" fw={500}>{viewMessage.senderName}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Date</Text>
                <Text size="sm">{viewMessage.sentAt ? dayjs(viewMessage.sentAt).format('MMMM D, YYYY h:mm A') : '-'}</Text>
              </div>
              <Badge size="sm" variant="light" color="teal">
                <Group gap={4}><IconInbox size={10} /> In-App</Group>
              </Badge>
            </Group>
            <Paper p="md" bg="gray.0" radius="sm">
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{viewMessage.body}</Text>
            </Paper>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setViewMessage(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Log Completion Modal ────────────────────────────────── */}
      <Modal
        opened={logModalOpen}
        onClose={closeLogModal}
        title={
          <Group gap="xs">
            <IconClipboardList size={16} color="#4a82c4" />
            <Text fw={600}>Log Completion</Text>
          </Group>
        }
        size="md"
      >
        {logTraining && (
          <Stack gap="md">
            {/* Training name */}
            <Paper p="sm" radius="sm" style={{ backgroundColor: '#f0f4fa', border: '1px solid #dbe4f0' }}>
              <Group gap="xs">
                <IconBook size={14} color="#4a82c4" />
                <Text size="sm" fw={600} c="#1a365d">{logTraining.name}</Text>
              </Group>
              {logTraining.category && (
                <Badge
                  color={CATEGORY_COLORS[logTraining.category] || 'gray'}
                  size="sm"
                  variant="light"
                  mt={6}
                >
                  {logTraining.category}
                </Badge>
              )}
              {logTraining.description && (
                <Text size="xs" c="dimmed" mt={6}>{logTraining.description}</Text>
              )}
            </Paper>

            {/* Completion date */}
            <DateInput
              label="Completion Date"
              description="When did you complete this training?"
              placeholder="Select date"
              value={logDate}
              onChange={setLogDate}
              maxDate={dayjs().format('YYYY-MM-DD')}
              required
              leftSection={<IconCalendar size={14} />}
            />

            {/* Notes */}
            <Textarea
              label="Notes"
              description="Optional details about where/how you completed this training"
              placeholder="e.g., Completed via FEMA Independent Study"
              value={logNotes}
              onChange={(e) => setLogNotes(e.currentTarget.value)}
              minRows={2}
              maxRows={4}
              autosize
            />

            {/* Actions */}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeLogModal} disabled={logSaving}>
                Cancel
              </Button>
              <Button
                color="blue"
                onClick={handleLogSave}
                loading={logSaving}
                leftSection={!logSaving ? <IconCheck size={14} /> : undefined}
              >
                Save Completion
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── View Details Modal ──────────────────────────────────── */}
      <Modal
        opened={viewModalOpen}
        onClose={closeViewModal}
        title={
          <Group gap="xs">
            <IconSchool size={16} color="#4a82c4" />
            <Text fw={600}>{viewTraining?.name || 'Training Details'}</Text>
          </Group>
        }
        size="md"
      >
        {viewTraining && viewRecord && (
          <Stack gap="md">
            {/* Status badge */}
            {viewStatus && (
              <Badge
                variant="light"
                size="sm"
                style={{
                  backgroundColor: viewStatus.color + '18',
                  color: viewStatus.color,
                  alignSelf: 'flex-start',
                }}
              >
                {viewStatus.label}
              </Badge>
            )}

            {/* Training info */}
            {viewTraining.description && (
              <Box>
                <Text size="xs" c="dimmed" fw={500} mb={2}>Description</Text>
                <Text size="sm">{viewTraining.description}</Text>
              </Box>
            )}

            {/* Dates */}
            <Group gap="xl">
              <Box>
                <Text size="xs" c="dimmed" fw={500}>Completed</Text>
                <Text size="sm" fw={600}>{formatDate(viewRecord.completedDate) || '\u2014'}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" fw={500}>Expires</Text>
                <Text size="sm" fw={600}>{formatDate(viewRecord.expiresDate) || 'Never'}</Text>
              </Box>
              {viewTraining.frequencyDays > 0 && (
                <Box>
                  <Text size="xs" c="dimmed" fw={500}>Frequency</Text>
                  <Text size="sm" fw={500}>{viewTraining.frequencyDays} days</Text>
                </Box>
              )}
            </Group>

            {/* Category */}
            {viewTraining.category && (
              <Box>
                <Text size="xs" c="dimmed" fw={500} mb={2}>Category</Text>
                <Badge
                  color={CATEGORY_COLORS[viewTraining.category] || 'gray'}
                  size="sm"
                  variant="light"
                >
                  {viewTraining.category}
                </Badge>
              </Box>
            )}

            {/* Notes */}
            {viewRecord.notes && (
              <Box>
                <Text size="xs" c="dimmed" fw={500} mb={2}>Notes</Text>
                <Paper p="sm" radius="sm" style={{ backgroundColor: '#f8f9fa', border: '1px solid #eee' }}>
                  <Text size="sm">{viewRecord.notes}</Text>
                </Paper>
              </Box>
            )}

            {/* Completion history for this training */}
            {myRecords.filter(r => r.trainingId === String(viewTraining.id) && r.completedDate).length > 1 && (
              <Box>
                <Text size="xs" c="dimmed" fw={500} mb={4}>Completion History</Text>
                <Stack gap={4}>
                  {myRecords
                    .filter(r => r.trainingId === String(viewTraining.id) && r.completedDate)
                    .sort((a, b) => {
                      const da = a.completedDate ? new Date(a.completedDate).getTime() : 0;
                      const db2 = b.completedDate ? new Date(b.completedDate).getTime() : 0;
                      return db2 - da;
                    })
                    .map((r) => {
                      const st = getStatusInfo(r, viewTraining);
                      return (
                        <Group key={r.id} justify="space-between" p={6} style={{ borderRadius: 6, background: '#f8f9fa' }}>
                          <Text size="xs">{formatDate(r.completedDate)}</Text>
                          <Group gap="xs">
                            {r.expiresDate && <Text size="xs" c="dimmed">Expires {formatDate(r.expiresDate)}</Text>}
                            <Badge variant="light" size="xs" style={{ backgroundColor: st.color + '18', color: st.color }}>
                              {st.label}
                            </Badge>
                          </Group>
                        </Group>
                      );
                    })}
                </Stack>
              </Box>
            )}

            {/* Actions */}
            <Group justify="flex-end" mt="sm">
              {viewIsExpiredOrExpiring && (
                <Button
                  variant="light"
                  color="orange"
                  size="sm"
                  onClick={handleRenew}
                  leftSection={<IconRefresh size={14} />}
                >
                  Renew
                </Button>
              )}
              <Button variant="default" onClick={closeViewModal}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
